import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWakeLock } from 'react-screen-wake-lock';
import { Api } from 'lavva.exalushome';
import { IDevice } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { ResponseResult } from 'lavva.exalushome/build/js/Services/FieldChangeResult';
import { IDeviceUpdateInfo } from 'lavva.exalushome/build/js/Services/Updates/IUpdateInfo';
import {
  IUpdatesService,
  UpdateErrorCode,
  UpdateProgressInfo,
} from 'lavva.exalushome/build/js/Services/Updates/IUpdatesService';
import { UpdatesService } from 'lavva.exalushome/build/js/Services/Updates/UpdatesService';
import ArrowButton from '../../../../../components/arrow-button';
import { useBackdropContext } from '../../../../../context/backdrop';
import { ROUTES } from '../../../../../routes/routes';
import { useUpdateErrors } from '../../../hooks/common/use-update-errors';
import { VersionDeviceButtonComponent } from '../components/version-device-button/version-device-button';
import { FeedbackUpdate } from '../types';
import { useUpdateProcess } from './use-update-process';

export const useDeviceUpdates = (device?: IDevice) => {
  const navigate = useNavigate();
  const { t } = useTranslation('device-info');
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const [deviceVersion, setDeviceVersion] = useState<string>('');
  const [retransmitterPopup, setRetransmitterPopup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [deviceUpdate, setDeviceUpdate] = useState<IDeviceUpdateInfo | null>(null);
  const [show, setShow] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<FeedbackUpdate | null>(null);
  const { handleUpdatesResponse, showUpdateError } = useUpdateErrors();
  const { request, release } = useWakeLock();

  useEffect(() => {
    request();

    return () => {
      release();
    };
  }, []);

  const getCurrentDeviceVersion = async () => {
    if (device) {
      turnOnBackdrop();
      const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);
      const result = await updateService.CheckDeviceVersionAsync(device);

      handleUpdatesResponse<string>(result, () => {
        setDeviceVersion(result as string);
        setShow(true);
        turnOffBackdrop();
      });
    }
  };

  const checkForDeviceUpdates = async () => {
    setDeviceUpdate(null);
    setFeedback(null);

    if (device) {
      turnOnBackdrop();
      const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);
      const result = await updateService.CheckDeviceUpdateAsync(device);

      if (typeof (result as ResponseResult<UpdateErrorCode>).Type === 'number') {
        const updateResult = showUpdateError(result as ResponseResult<UpdateErrorCode>, true);

        if (updateResult) {
          setFeedback(updateResult);
        }
      } else {
        setDeviceUpdate(result as IDeviceUpdateInfo);
        turnOffBackdrop();
      }
    }
  };

  const updateStart = async (force: boolean) => {
    if (device) {
      setLoading(true);
      const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);

      const result = await updateService.InstallDeviceUpdateAsync(
        device,
        (updateProgress: UpdateProgressInfo) => onUpdateDeviceProgress(updateProgress, deviceUpdate),
        undefined,
        force,
      );

      setLoading(false);
      if ((result as ResponseResult<UpdateErrorCode>).Type) {
        if ((result as ResponseResult<UpdateErrorCode>).Type === UpdateErrorCode.RetransmitterFound) {
          setRetransmitterPopup(true);
        } else {
          showUpdateError(result as ResponseResult<UpdateErrorCode>);
        }
      }
    }
  };

  const { onUpdateDeviceProgress } = useUpdateProcess();

  const VersionDeviceButton = useMemo(
    () => (
      <VersionDeviceButtonComponent
        show={show}
        setShow={setShow}
        deviceVersion={deviceVersion}
        getCurrentDeviceVersion={getCurrentDeviceVersion}
      />
    ),
    [deviceVersion, show],
  );

  const CheckUpdateDeviceButton = useMemo(
    () => (
      <ArrowButton
        className="m-b-24"
        title={t('exalus.params.Version.update')}
        onClick={() => navigate(ROUTES.DeviceUpdate(device?.Guid))}
      />
    ),
    [deviceUpdate, show, device],
  );

  return {
    VersionDeviceButton,
    CheckUpdateDeviceButton,
    deviceUpdate,
    feedback,
    retransmitterPopup,
    loading,
    checkForDeviceUpdates,
    updateStart,
    setRetransmitterPopup,
  };
};
